import React, { useMemo, useRef, useState } from 'react';
import { formatDate, todayIso } from '../lib/format';
import { joinNames } from '../lib/reportDraft';
import { uid } from './ui';

/*
 * Unesi rezultat — the phone-first way in.
 *
 * Built around how results are actually recorded: a day, one pairing, then a
 * run of matches. And around what the data shows — the winner's score is
 * almost never news (Briškula always 4, Bela always 41, Trešeta 41 in 96%,
 * boće 13 in 99%). So you tap who won and type only the loser's score; the
 * winner's is filled in and stays editable for the rare exception.
 *
 * No A/B anywhere: teams are named by the players in them.
 */

const TARGET = { 'Trešeta': 41, 'Briškula': 4, 'Bela': 41, 'Bela trešeta': 41 };
const BOCCE_TARGET = 13;

// ---------------------------------------------------------------- building blocks

function Screen({ title, sub, children, onBack, footer, step, steps }) {
  return (
    <div className="flex min-h-[100dvh] flex-col bg-white">
      <header className="sticky top-0 z-10 border-b border-slate-200 bg-white px-4 pb-3 pt-4">
        <div className="flex items-center gap-3">
          {onBack && (
            <button
              type="button"
              onClick={onBack}
              aria-label="Natrag"
              className="-ml-2 flex h-11 w-11 items-center justify-center rounded-full text-2xl text-slate-500 active:bg-slate-100"
            >
              ←
            </button>
          )}
          <div className="min-w-0 flex-1">
            <h1 className="truncate text-xl font-black text-slate-900">{title}</h1>
            {sub && <p className="truncate text-sm text-slate-500">{sub}</p>}
          </div>
          {steps && <span className="shrink-0 text-xs font-bold text-slate-400">{step}/{steps}</span>}
        </div>
      </header>

      <div className="flex-1 px-4 py-5">{children}</div>

      {footer && (
        <div className="sticky bottom-0 border-t border-slate-200 bg-white px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
          {footer}
        </div>
      )}
    </div>
  );
}

function BigButton({ children, onClick, tone = 'plain', disabled, className = '' }) {
  const tones = {
    plain: 'bg-white text-slate-900 border-2 border-slate-300 active:bg-slate-100',
    primary: 'bg-[#009FE3] text-white border-2 border-[#009FE3] active:bg-[#0089c4] disabled:bg-slate-300 disabled:border-slate-300',
    dark: 'bg-slate-900 text-white border-2 border-slate-900 active:bg-slate-700',
    on: 'bg-[#009FE3] text-white border-2 border-[#009FE3]',
  };
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`w-full rounded-xl px-4 py-4 text-lg font-bold transition ${tones[tone]} ${className}`}
    >
      {children}
    </button>
  );
}

/** Big tappable player name. */
function PlayerChip({ name, selected, disabled, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`rounded-xl border-2 px-3 py-4 text-lg font-bold transition ${
        selected
          ? 'border-[#009FE3] bg-[#009FE3] text-white'
          : disabled
            ? 'border-slate-200 bg-slate-50 text-slate-300'
            : 'border-slate-300 bg-white text-slate-900 active:bg-slate-100'
      }`}
    >
      {name}
    </button>
  );
}

/** Score box that opens the numeric keypad on a phone. */
function ScoreInput({ value, onChange, label, highlight }) {
  return (
    <label className="flex flex-col items-center gap-1">
      <span className="max-w-[7rem] text-center text-[11px] font-bold uppercase leading-tight tracking-wider text-slate-500">{label}</span>
      <input
        type="number"
        inputMode="numeric"
        pattern="[0-9]*"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={(e) => e.target.select()}
        className={`w-24 rounded-xl border-2 py-3 text-center text-3xl font-black tabular-nums outline-none ${
          highlight ? 'border-[#009FE3] bg-[#009FE3]/5 text-slate-900' : 'border-slate-300 bg-white text-slate-900'
        } focus:border-[#009FE3]`}
      />
    </label>
  );
}

// ---------------------------------------------------------------- one game

/**
 * Winner is a tap; only the loser's score is typed. The winner's score sits
 * behind a small "nije bilo N" link for the rare match that didn't end on the
 * usual number.
 */
function GameCard({ title, teamA, teamB, game, onChange, onRemove, target }) {
  // joinNames gives 'Ago, Pero i Bimbo', not 'Ago i Pero i Bimbo'
  const [editWinner, setEditWinner] = useState(false);
  const win = game.winner; // 'A' | 'B' | null

  const set = (patch) => onChange({ ...game, ...patch });

  return (
    <div className="rounded-2xl border-2 border-slate-200 p-4">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-sm font-black uppercase tracking-wide text-slate-700">{title}</h3>
        {onRemove && (
          <button type="button" onClick={onRemove} className="text-sm font-medium text-slate-400 active:text-red-600">
            makni
          </button>
        )}
      </div>

      <p className="mb-2 text-sm font-semibold text-slate-500">Tko je dobio?</p>
      <div className="mb-4 grid gap-2">
        <BigButton tone={win === 'A' ? 'on' : 'plain'} onClick={() => set({ winner: 'A' })}>
          {joinNames(teamA)}
        </BigButton>
        <BigButton tone={win === 'B' ? 'on' : 'plain'} onClick={() => set({ winner: 'B' })}>
          {joinNames(teamB)}
        </BigButton>
      </div>

      {win && (
        <div className="border-t border-slate-100 pt-4">
          <div className="flex items-end justify-center gap-4">
            <ScoreInput
              label={joinNames(win === 'A' ? teamA : teamB)}
              value={game.winnerScore}
              onChange={(v) => set({ winnerScore: v })}
              highlight
            />
            <span className="pb-4 text-2xl font-black text-slate-300">:</span>
            <ScoreInput
              label={joinNames(win === 'A' ? teamB : teamA)}
              value={game.loserScore}
              onChange={(v) => set({ loserScore: v })}
            />
          </div>

          {!editWinner && String(game.winnerScore) === String(target) && (
            <button
              type="button"
              onClick={() => setEditWinner(true)}
              className="mx-auto mt-3 block text-sm text-slate-400 underline active:text-slate-700"
            >
              nije završilo na {target}?
            </button>
          )}
          <p className="mt-3 text-center text-xs text-slate-400">
            Upiši samo koliko je imao gubitnik.
          </p>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- wizard

export default function QuickAdd({ roster, karte, boce, onSave, onClose }) {
  const [step, setStep] = useState('date');
  const [date, setDate] = useState(todayIso());
  const [sport, setSport] = useState(null);
  const [teamA, setTeamA] = useState([]);
  const [teamB, setTeamB] = useState([]);
  const [games, setGames] = useState([]);
  const [bocce, setBocce] = useState({ winner: null, winnerScore: BOCCE_TARGET, loserScore: '' });
  const [savedToday, setSavedToday] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const topRef = useRef(null);

  const yesterday = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    const p = (v) => String(v).padStart(2, '0');
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
  }, []);

  // Pairings used before, so a repeat day is two taps instead of eight.
  const recentPairs = useMemo(() => {
    const src = sport === 'karte' ? karte : boce;
    const seen = new Map();
    [...src].sort((a, b) => String(b.date).localeCompare(String(a.date))).forEach((m) => {
      const key = `${[...m.teamA].sort().join(',')}|${[...m.teamB].sort().join(',')}`;
      if (!seen.has(key) && seen.size < 4) seen.set(key, { teamA: m.teamA, teamB: m.teamB });
    });
    return [...seen.values()];
  }, [sport, karte, boce]);

  const startGames = () => [{ name: 'Trešeta', winner: null, winnerScore: TARGET['Trešeta'], loserScore: '' }];

  const reset = (keepTeams) => {
    setGames(startGames());
    setBocce({ winner: null, winnerScore: BOCCE_TARGET, loserScore: '' });
    if (!keepTeams) { setTeamA([]); setTeamB([]); }
    setError(null);
    topRef.current?.scrollTo?.(0, 0);
  };

  const teamsOk = sport === 'karte'
    ? teamA.length === 2 && teamB.length === 2
    : teamA.length > 0 && teamB.length > 0;

  const num = (v, fallback = 0) => {
    const n = parseInt(v, 10);
    return Number.isFinite(n) ? n : fallback;
  };

  const karteReady = games.length > 0
    && games.every((g) => g.winner && String(g.loserScore).trim() !== '')
    && (() => {
      let a = 0; let b = 0;
      games.forEach((g) => { if (g.winner === 'A') a += 1; else b += 1; });
      return a !== b; // a drawn match can't be saved without another game
    })();

  const bocceReady = bocce.winner && String(bocce.loserScore).trim() !== '';

  const buildMatch = () => {
    if (sport === 'karte') {
      const built = games.map((g) => {
        const w = num(g.winnerScore, TARGET[g.name] ?? 0);
        const l = num(g.loserScore);
        return { name: g.name, a: g.winner === 'A' ? w : l, b: g.winner === 'A' ? l : w };
      });
      return { id: uid('k'), date, teamA, teamB, games: built, pointsFor: [], autoWinner: true };
    }
    const w = num(bocce.winnerScore, BOCCE_TARGET);
    const l = num(bocce.loserScore);
    return {
      id: uid('b'), date, teamA, teamB,
      scoreA: bocce.winner === 'A' ? w : l,
      scoreB: bocce.winner === 'A' ? l : w,
      status: 'KRAJ', note: '', pointsFor: [], autoWinner: true,
    };
  };

  const saveMatch = async () => {
    setSaving(true);
    setError(null);
    const match = buildMatch();
    try {
      await onSave(sport, match);
      setSavedToday((s) => [...s, match]);
      setStep('after');
    } catch (e) {
      setError(e.code === 'permission-denied'
        ? 'Nemaš prava za pisanje. Provjeri pravila u Firebaseu.'
        : (e.message || 'Greška pri spremanju.'));
    } finally {
      setSaving(false);
    }
  };

  const gamesWonLabel = () => {
    let a = 0; let b = 0;
    games.forEach((g) => { if (g.winner === 'A') a += 1; else if (g.winner === 'B') b += 1; });
    return `${a}:${b}`;
  };

  // ------------------------------------------------------------- screens

  if (step === 'date') {
    return (
      <Screen title="Novi rezultat" sub="Kojeg dana se igralo?" onBack={onClose} step={1} steps={4}
        footer={<BigButton tone="primary" onClick={() => setStep('sport')}>Dalje</BigButton>}>
        <div className="grid gap-3">
          <BigButton tone={date === todayIso() ? 'on' : 'plain'} onClick={() => setDate(todayIso())}>
            Danas <span className="font-normal opacity-70">· {formatDate(todayIso())}</span>
          </BigButton>
          <BigButton tone={date === yesterday ? 'on' : 'plain'} onClick={() => setDate(yesterday)}>
            Jučer <span className="font-normal opacity-70">· {formatDate(yesterday)}</span>
          </BigButton>
          <label className="mt-2 flex flex-col gap-2">
            <span className="text-sm font-semibold text-slate-500">Neki drugi dan</span>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="w-full rounded-xl border-2 border-slate-300 px-4 py-4 text-lg font-bold text-slate-900 outline-none focus:border-[#009FE3]"
            />
          </label>
        </div>
      </Screen>
    );
  }

  if (step === 'sport') {
    return (
      <Screen title={formatDate(date)} sub="Što se igralo?" onBack={() => setStep('date')} step={2} steps={4}>
        <div className="grid gap-3">
          <BigButton tone="plain" className="!py-8 !text-2xl"
            onClick={() => { setSport('karte'); reset(false); setStep('teams'); }}>
            🃏 Karte
          </BigButton>
          <BigButton tone="plain" className="!py-8 !text-2xl"
            onClick={() => { setSport('boce'); reset(false); setStep('teams'); }}>
            🟢 Boće
          </BigButton>
        </div>
      </Screen>
    );
  }

  if (step === 'teams') {
    const pick = (name, side) => {
      if (side === 'A') {
        setTeamB((b) => b.filter((n) => n !== name));
        setTeamA((a) => (a.includes(name) ? a.filter((n) => n !== name) : [...a, name]));
      } else {
        setTeamA((a) => a.filter((n) => n !== name));
        setTeamB((b) => (b.includes(name) ? b.filter((n) => n !== name) : [...b, name]));
      }
    };
    const need = sport === 'karte' ? '2 na 2' : 'koliko vas je bilo';

    return (
      <Screen
        title="Tko je igrao?"
        sub={`${sport === 'karte' ? 'Karte' : 'Boće'} · ${formatDate(date)} · ${need}`}
        onBack={() => setStep('sport')}
        step={3} steps={4}
        footer={
          <BigButton tone="primary" disabled={!teamsOk} onClick={() => { reset(true); setStep('score'); }}>
            {teamsOk ? 'Dalje' : sport === 'karte' ? 'Treba 2 na 2' : 'Odaberi obje ekipe'}
          </BigButton>
        }
      >
        {recentPairs.length > 0 && teamA.length === 0 && teamB.length === 0 && (
          <div className="mb-6">
            <p className="mb-2 text-sm font-semibold text-slate-500">Zadnji put su igrali</p>
            <div className="grid gap-2">
              {recentPairs.map((pair, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => { setTeamA(pair.teamA); setTeamB(pair.teamB); }}
                  className="rounded-xl border-2 border-slate-200 bg-slate-50 px-4 py-3 text-left text-base font-semibold text-slate-700 active:bg-slate-100"
                >
                  {joinNames(pair.teamA)} <span className="text-slate-400"> — </span> {joinNames(pair.teamB)}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="mb-6">
          <p className="mb-2 flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-slate-500">
            <span className="inline-block h-3 w-3 rounded-full bg-[#009FE3]" /> Prva ekipa
          </p>
          <div className="grid grid-cols-2 gap-2">
            {roster.map((n) => (
              <PlayerChip
                key={n} name={n}
                selected={teamA.includes(n)}
                disabled={teamB.includes(n)}
                onClick={() => pick(n, 'A')}
              />
            ))}
          </div>
        </div>

        <div>
          <p className="mb-2 flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-slate-500">
            <span className="inline-block h-3 w-3 rounded-full bg-slate-800" /> Druga ekipa
          </p>
          <div className="grid grid-cols-2 gap-2">
            {roster.map((n) => (
              <PlayerChip
                key={n} name={n}
                selected={teamB.includes(n)}
                disabled={teamA.includes(n)}
                onClick={() => pick(n, 'B')}
              />
            ))}
          </div>
        </div>
      </Screen>
    );
  }

  if (step === 'score') {
    const header = `${joinNames(teamA)} — ${joinNames(teamB)}`;
    const usedNames = games.map((g) => g.name);
    const canAddBela = sport === 'karte' && !usedNames.includes('Bela') && games.length < 4;
    const canAddBriskula = sport === 'karte' && !usedNames.includes('Briškula') && games.length < 4;

    return (
      <Screen
        title="Rezultat"
        sub={header}
        onBack={() => setStep('teams')}
        step={4} steps={4}
        footer={
          <div className="grid gap-2">
            {error && <p className="text-center text-sm font-semibold text-red-600">{error}</p>}
            {sport === 'karte' && games.length > 1 && !karteReady && games.every((g) => g.winner) && (
              <p className="text-center text-sm text-amber-700">
                {gamesWonLabel()} u partijama — dodaj još jednu da se zna tko je dobio meč.
              </p>
            )}
            <BigButton
              tone="primary"
              disabled={saving || (sport === 'karte' ? !karteReady : !bocceReady)}
              onClick={saveMatch}
            >
              {saving ? 'Spremam…' : 'Spremi meč'}
            </BigButton>
          </div>
        }
      >
        <div className="grid gap-4">
          {sport === 'karte' ? (
            <>
              {games.map((g, i) => (
                <GameCard
                  key={i}
                  title={g.name}
                  teamA={teamA} teamB={teamB}
                  game={g}
                  target={TARGET[g.name] ?? 41}
                  onChange={(next) => setGames((gs) => gs.map((x, xi) => (xi === i ? next : x)))}
                  onRemove={games.length > 1 ? () => setGames((gs) => gs.filter((_, xi) => xi !== i)) : undefined}
                />
              ))}
              <div className="grid grid-cols-2 gap-2">
                {canAddBriskula && (
                  <BigButton tone="plain" className="!py-3 !text-base"
                    onClick={() => setGames((gs) => [...gs, { name: 'Briškula', winner: null, winnerScore: TARGET['Briškula'], loserScore: '' }])}>
                    + Briškula
                  </BigButton>
                )}
                {canAddBela && (
                  <BigButton tone="plain" className="!py-3 !text-base"
                    onClick={() => setGames((gs) => [...gs, { name: 'Bela', winner: null, winnerScore: TARGET['Bela'], loserScore: '' }])}>
                    + Bela
                  </BigButton>
                )}
              </div>
            </>
          ) : (
            <GameCard
              title="Boće"
              teamA={teamA} teamB={teamB}
              game={bocce}
              target={BOCCE_TARGET}
              onChange={setBocce}
            />
          )}
        </div>
      </Screen>
    );
  }

  // step === 'after'
  const last = savedToday[savedToday.length - 1];
  const lastLine = last && (last.games
    ? last.games.map((g) => `${g.a}:${g.b}`).join('  ')
    : `${last.scoreA}:${last.scoreB}`);

  return (
    <Screen title="Spremljeno ✓" sub={`${formatDate(date)} · ukupno ${savedToday.length} ${savedToday.length === 1 ? 'meč' : 'mečeva'}`}>
      <div className="mb-6 rounded-2xl border-2 border-emerald-200 bg-emerald-50 p-4">
        <p className="text-sm font-semibold text-emerald-900">
          {joinNames(last.teamA)} — {joinNames(last.teamB)}
        </p>
        <p className="mt-1 text-2xl font-black tabular-nums text-emerald-900">{lastLine}</p>
      </div>

      <p className="mb-3 text-sm font-semibold text-slate-500">Što dalje?</p>
      <div className="grid gap-3">
        <BigButton tone="primary" onClick={() => { reset(true); setStep('score'); }}>
          Još jedan meč, isti igrači
        </BigButton>
        <BigButton tone="plain" onClick={() => { reset(false); setStep('teams'); }}>
          Drugi igrači, isti dan
        </BigButton>
        <BigButton tone="plain" onClick={() => { reset(false); setStep('sport'); }}>
          Prebaci na {sport === 'karte' ? 'boće' : 'karte'}
        </BigButton>
        <BigButton tone="dark" onClick={onClose}>Gotovo</BigButton>
      </div>

      {savedToday.length > 1 && (
        <div className="mt-8">
          <p className="mb-2 text-sm font-semibold text-slate-500">Uneseno danas</p>
          <ul className="divide-y divide-slate-100 rounded-xl border border-slate-200">
            {savedToday.map((m) => (
              <li key={m.id} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
                <span className="truncate text-slate-600">
                  {joinNames(m.teamA)} / {joinNames(m.teamB)}
                </span>
                <span className="shrink-0 font-bold tabular-nums text-slate-900">
                  {m.games ? m.games.map((g) => `${g.a}:${g.b}`).join(' ') : `${m.scoreA}:${m.scoreB}`}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </Screen>
  );
}
